/**
 * MindMap — Buzan-style curved-branch mind map rendered with markmap-view.
 *
 * The backend emits `{ center, branches: [{ label, subs: [] }] }`.
 * We convert that to Markdown and hand it to markmap-lib's Transformer +
 * markmap-view's Markmap renderer, which produces:
 *  - a big central topic
 *  - organic curved branches, one colour per branch
 *  - hierarchical sub-branches that fan out radially
 */
import { useEffect, useRef, useState } from "react";
import { Transformer } from "markmap-lib";
import { Markmap, deriveOptions } from "markmap-view";
import { RefreshCw, Maximize2, Download } from "lucide-react";
import { latexToUnicode } from "@/lib/latex-unicode";

const transformer = new Transformer();

/** Escape markdown-breaking characters in text that will be inlined in a `.md` string. */
function _esc(s) {
  // Run LaTeX→Unicode first so `\times`, `^{2}`, `H_{2}O` etc. become real
  // symbols BEFORE we escape any remaining backslashes for markdown safety.
  const converted = latexToUnicode(String(s || ""));
  return converted.replace(/([\\*_`~[\]])/g, "\\$1").trim();
}

/** Normalise a sub node — accepts old string form or new {label, note}. */
function _normSub(s) {
  if (s == null) return null;
  if (typeof s === "string") return { label: s.trim(), note: "" };
  return { label: (s.label || "").trim(), note: (s.note || "").trim() };
}

/**
 * Render a node as `**label** — note` when a note is present, otherwise just the label.
 * Result is inline-safe markdown for a heading/bullet.
 */
function _renderNode(label, note) {
  const l = _esc(label);
  const n = _esc(note);
  if (!l) return "";
  if (!n) return l;
  return `**${l}** — <span class="mm-note">${n}</span>`;
}

function toMarkdown(mindMap) {
  const centerLabel = mindMap?.center || "Mind Map";
  const centerNote = mindMap?.center_note || "";
  const branches = (mindMap?.branches || []).filter((b) => b?.label).slice(0, 8);

  const lines = [`# ${_renderNode(centerLabel, centerNote)}`];
  for (const b of branches) {
    lines.push(`## ${_renderNode(b.label, b.note)}`);
    const subs = (b.subs || [])
      .map(_normSub)
      .filter((s) => s && s.label)
      .slice(0, 6);
    for (const s of subs) {
      lines.push(`- ${_renderNode(s.label, s.note)}`);
    }
  }
  return lines.join("\n");
}

export default function MindMap({ mindMap }) {
  const svgRef = useRef(null);
  const mmRef = useRef(null);
  const wrapperRef = useRef(null);
  const [error, setError] = useState(null);

  const md = mindMap ? toMarkdown(mindMap) : null;

  // Build / rebuild the markmap whenever the source markdown changes.
  useEffect(() => {
    if (!md || !svgRef.current) return;
    try {
      const { root } = transformer.transform(md);
      // Reset any previous render
      svgRef.current.innerHTML = "";
      const options = deriveOptions({
        colorFreezeLevel: 2,       // stable colour per top-level branch
        initialExpandLevel: -1,    // expand everything
        duration: 500,
        maxWidth: 220,             // wrap long labels
        spacingVertical: 12,
        spacingHorizontal: 90,
        paddingX: 12,
      });
      const mm = Markmap.create(svgRef.current, options, root);
      mmRef.current = mm;
      // Give layout a tick before fit
      setTimeout(() => mm.fit(), 60);
      setError(null);
    } catch (e) {
      console.error("MindMap render failed", e);
      setError(e.message || String(e));
    }
  }, [md]);

  // Re-fit on container resize
  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(() => mmRef.current?.fit());
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  if (!mindMap?.center) return null;

  const rescale = () => mmRef.current?.fit();
  const fullscreen = () => {
    const el = wrapperRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };
  const downloadSvg = () => {
    if (!svgRef.current) return;
    const src = new XMLSerializer().serializeToString(svgRef.current);
    const blob = new Blob([src], { type: "image/svg+xml;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(mindMap.center || "mind-map").replace(/\s+/g, "-").toLowerCase()}.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div
      data-testid="mind-map"
      ref={wrapperRef}
      className="mind-map w-full border-2 border-black bg-white relative"
    >
      <div className="flex items-center justify-between px-4 py-2 border-b-2 border-black bg-black text-white">
        <span className="font-mono text-[10px] uppercase tracking-widest">/ Mind Map</span>
        <div className="flex items-center gap-1">
          <button
            onClick={rescale}
            className="p-1.5 hover:bg-white/10"
            title="Fit to view"
            data-testid="mind-map-fit"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={downloadSvg}
            className="p-1.5 hover:bg-white/10"
            title="Download SVG"
            data-testid="mind-map-download"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={fullscreen}
            className="p-1.5 hover:bg-white/10"
            title="Fullscreen"
            data-testid="mind-map-fs"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {error && (
        <p className="p-4 font-mono text-xs text-red-600">Mind map render error: {error}</p>
      )}

      <svg
        ref={svgRef}
        className="w-full h-[520px] block bg-[#FAFAFA]"
        style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
      />

      <p className="px-4 py-2 border-t border-black/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        Scroll / pinch to zoom · drag to pan · click a branch to collapse
      </p>
    </div>
  );
}
